/*----------------------------------
- MARKETING PAGES: FAQ RICH TEXT
----------------------------------*/

/**
 * One FAQ answer with its glossary terms: the plain runs as text, the first use of each term as the marketing
 * `GlossaryTerm` (`dfn.wp-gl` holding its card, the catalog's definition and its glossary row), as the mock draws every
 * answer.
 *
 * Decision: the compare page's recipe (`compare/components/CompareRichText.tsx`) rather than a `GlossarySection`
 * scope. One answer is one section with its own `seen` set: a reader
 * opening one question never saw the others, so a term is explained again in each answer that uses it.
 *
 * The support address in an answer opens the mail app (`a.ud-link.pr-mail`, as the mock draws it): the address is the
 * legal catalog's, matched as a whole string, so the answer copy stays plain text in the shared FAQ catalog.
 *
 * Shared by Home and the pricing page (their answers come from one FAQ catalog).
 *
 * @docs docs/features/website-marketing-layer
 * @rule Never inside a heading, a link or a button: a `dfn` with a card inside a link would nest interactive content.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { splitGlossaryText } from '@uniquedomains/components/glossary/split';
import { glossaryTermHref } from '@uniquedomains/components/glossary/terms';
import { TextLink } from '@uniquedomains/components/kit/text-link/TextLink';
import { GlossaryTerm } from '@uniquedomains/components/marketing/glossary-term/GlossaryTerm';

import { LEGAL_ENTITY } from '@/common/catalogs/legal/entity';

/*----------------------------------
- HELPERS
----------------------------------*/

/** A plain run with the support address turned into a mail link (the rest stays text). */
const renderPlainRun = (run: string, key: string) =>
    run.split(LEGAL_ENTITY.contactEmail).flatMap((part, index) =>
        index === 0
            ? [part]
            : [
                  <TextLink key={`${key}-mail-${index}`} class="pr-mail" href={`mailto:${LEGAL_ENTITY.contactEmail}`}>
                      {LEGAL_ENTITY.contactEmail}
                  </TextLink>,
                  part,
              ],
    );

/*----------------------------------
- COMPONENT
----------------------------------*/

const FaqRichText = ({ text }: { text: string }) => (
    <>
        {splitGlossaryText(text, { seen: new Set<string>() }).map((segment, index) =>
            typeof segment === 'string' ? (
                renderPlainRun(segment, String(index))
            ) : (
                <GlossaryTerm key={`${segment.term.id}-${index}`} title={segment.term.term} definition={segment.term.definition} href={glossaryTermHref(segment.term)}>
                    {segment.text}
                </GlossaryTerm>
            ),
        )}
    </>
);

export default FaqRichText;
